import { QueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import type { Request } from 'express';

import getMatchedRoute from '@rover/react-app/src/server/renderPage/getMatchedRoute';
import {
  apiBundleOfferingList,
  getApiBundleOfferingListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-bundle-offering-list';
import {
  apiFrontendConfigurationRetrieve,
  getApiFrontendConfigurationRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-configuration-retrieve';
import {
  apiNearbyTrainerCountRetrieve,
  getApiNearbyTrainerCountRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-nearby-trainer-count-retrieve';
import {
  apiPersonDetailImagesList,
  getApiPersonDetailImagesListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-person-detail-images-list';
import {
  apiPublicTrainerProfileRetrieve,
  getApiPublicTrainerProfileRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-public-trainer-profile-retrieve';
import { HttpError } from '@rover/rsdk/src/errors';
import { FLAGS } from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import httpContext from '@rover/rsdk/src/modules/Network/httpContext';
import { Http403Error, Http404Error, Http500Error } from '@rover/utilities/HttpError';

import { getShouldPrefetchNearbyTrainerCounts } from '../components/ShowMoreTrainersLink/utils';

export const publicTrainerProfileDataPrefetch = async (
  queryClient: QueryClient,
  req: Request
): Promise<void> => {
  const res = httpContext.getRes();

  const {
    pageRoute: { match },
  } = getMatchedRoute(req.path);

  let data;
  let bundleChangesActive = false;
  let shouldPrefetchNearbyTrainerCounts = false;
  try {
    const { flags } = await queryClient.fetchQuery({
      queryKey: getApiFrontendConfigurationRetrieveQueryKey(),
      queryFn: () => apiFrontendConfigurationRetrieve(),
    });

    bundleChangesActive = flags[FLAGS.ROLLOUT_PROVIDER_BUNDLE_OFFERINGS];
    shouldPrefetchNearbyTrainerCounts =
      flags[FLAGS.ROLLOUT_PUBLIC_TRAINER_PROFILE_SEO] &&
      getShouldPrefetchNearbyTrainerCounts(req.query);

    data = await queryClient.fetchQuery({
      queryKey: getApiPublicTrainerProfileRetrieveQueryKey(match.params.slug),
      queryFn: () => apiPublicTrainerProfileRetrieve(match.params.slug),
    });
  } catch (error) {
    if (error instanceof HttpError) {
      if (error.statusCode === 404) {
        throw new Http404Error(`Trainer with slug ${match.params.slug} not found.`);
      } else if (error.statusCode === 403) {
        throw new Http403Error(`Attempt to access Trainer Profile with relevant flag turned off`);
      } else if (error.statusCode === 301) {
        const redirectUrl = (error.res as AxiosResponse).headers.location;
        const queryParams = req.query;
        let url = redirectUrl;
        if (Object.keys(queryParams).length > 0) {
          url += `?${new URLSearchParams(queryParams as Record<string, string>).toString()}`;
        }
        res?.redirect(url);
        return;
      }
    }
    throw new Http500Error(
      `Server error in fetching Trainer Profile for user with slug ${match.params.slug}.`
    );
  }
  await Promise.all([
    queryClient.fetchQuery({
      queryKey: getApiPersonDetailImagesListQueryKey(data.opk, { page: 1, pageSize: 25 }),
      queryFn: () => apiPersonDetailImagesList(data.opk, { page: 1, pageSize: 25 }),
    }),
    ...(shouldPrefetchNearbyTrainerCounts
      ? [
          queryClient.prefetchQuery({
            queryKey: getApiNearbyTrainerCountRetrieveQueryKey(match.params.slug, {
              searchNearUser: false,
            }),
            queryFn: () =>
              apiNearbyTrainerCountRetrieve(match.params.slug, {
                searchNearUser: false,
              }),
          }),
          queryClient.prefetchQuery({
            queryKey: getApiNearbyTrainerCountRetrieveQueryKey(match.params.slug, {
              searchNearUser: true,
            }),
            queryFn: () =>
              apiNearbyTrainerCountRetrieve(match.params.slug, {
                searchNearUser: true,
              }),
          }),
        ]
      : []),
    ...(bundleChangesActive
      ? [
          queryClient.prefetchQuery({
            queryKey: getApiBundleOfferingListQueryKey(match.params.slug, 'dog-training'),
            queryFn: () => apiBundleOfferingList(match.params.slug, 'dog-training'),
          }),
        ]
      : []),
  ]);
};
